import { type JSX, useState } from 'react';
import { Alert, Form, Modal } from 'react-bootstrap';

import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { Button } from 'Components/Buttons/Buttons';
import { ModalHeader } from 'Components/Modal/ModalHeader';
import { ICON_CLOSE } from 'Config/Icons';
import { iso8601ToJsDate, jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { ThirdPartyService, UpdateServiceRequest } from 'Models/TPRM';

import { IrqTargetCompletionDateSelection, getIrqTargetCompletionDateSelectionProps } from '../Components/IrqTargetCompletionDateSelection/IrqTargetCompletionDateSelection';

export interface ServiceInherentRiskQuestionnaireTargetCompletionDateModalProps {
    hideModal: () => void;
    service: ThirdPartyService;
    targetDateSet: () => void;
    tprmApi: ClientTPRMApi;
}

interface FormState {
    isLoading?: boolean;
    successMessage?: string;
    failureMessage?: string;
}

// TODO [TPRM - Service Dashboard]: DRY this with the UI/logic in ServiceDashboard for the start/restart modals, if possible. Lots was copied from there and pasted here.
/**
 * This modal allows the user to set the target completion date for the Inherent Risk Questionnaire for a specific third-party service.
 * This is not the modal used to set the default, organization-wide target completion schedules for all inherent risk questionnaires.
 */
export const ServiceInherentRiskQuestionnaireTargetCompletionDateModal = (props: ServiceInherentRiskQuestionnaireTargetCompletionDateModalProps): JSX.Element => {
    const [formState, setFormState] = useState<FormState>({});
    const [selectedIrqTargetCompletionDate, setSelectedIrqTargetCompletionDate] = useState<Date | undefined>(props.service.assessment_workflow_setup.irq_target_completion_date ? iso8601ToJsDate(props.service.assessment_workflow_setup.irq_target_completion_date) : undefined);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();
        try {
            setFormState({ isLoading: true });
            const request: UpdateServiceRequest = {
                name: props.service.name,
                description: props.service.description,
                vendor_service_manager_user_id: props.service.vendor_service_manager_user_id,
                assessment_due_date: props.service.assessment_workflow_setup.due_date,
                delegates: props.service.delegates,
                irq_target_completion_date: selectedIrqTargetCompletionDate ? jsDateToIso8601(selectedIrqTargetCompletionDate) : undefined,
                responsible_organization: props.service.responsible_organization,
                tags: props.service.tags,
                vendor_contacts: props.service.vendor_contacts,
            };
            await props.tprmApi.updateThirdPartyService(request, props.service.vendor_id, props.service.id);
            props.targetDateSet();
            setFormState({ successMessage: 'Inherent risk questionnaire target completion date saved.', isLoading: false });
        } catch (error) {
            handleRequestError(error);
        }
    };

    const handleRequestError = (error: Error) => {
        setFormState({ failureMessage: error.message, isLoading: false });
    };

    return (
        <Modal show onHide={props.hideModal} size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
            <Modal.Body className={'modalFromBody'}>
                {formState.successMessage && <Alert variant="success">{formState.successMessage}</Alert>}
                {formState.failureMessage && <Alert variant="danger">{formState.failureMessage}</Alert>}
                <Form noValidate onSubmit={handleSubmit}>
                    <ModalHeader text="Schedule Inherent Risk Questionnaire" />
                    <IrqTargetCompletionDateSelection
                        {...getIrqTargetCompletionDateSelectionProps(
                            {
                                action: 'updateExistingSelection',
                                currentAssessmentWorkflowIrqTargetCompletionDate: props.service.assessment_workflow_setup.irq_target_completion_date,
                            },
                            setSelectedIrqTargetCompletionDate
                        )}
                    />
                    <div className={'modalFormButtonContainer'}>
                        <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                            Close
                        </Button>
                        <Button variant="submit" isLoading={formState.isLoading === true} loadingText="Saving...">
                            Save
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};
